import Promise from 'bluebird';
import _ from 'underscore';
import { ResponsiveAd } from '@bingads-webui/campaign-odata-schemas';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { getODataConfig } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { getLogVersion } from '@bingads-webui-campaign-react/asset-library-model';
import { newVideoFromMTObject } from '@bingads-webui-campaign-react/image-experience-video-part';
import {
  getLoggerWithoutPageInfo,
  createVideoRepository,
  createImageRepository,
  createFolderRepository,
  uploadVideoMetadata,
} from '@bingads-webui-campaign-react/asset-library-services';

import { isBulkWrappedResponse, getBulkUploadErrorType, modelToViewModelForBulkErrors, ERROR_ENTITY } from './common';
import { bulkModelToViewModelForAd, splitUpdateAndCreateAds, viewModelToModelForAd } from './native-ad';

const GENERIC_ERROR_MESSAGE_KEY = _TL_("We've encountered an error and can't continue. Please contact support for further assistance.");
const adEDM = edmCampaign.types.resolve('Model.ResponsiveAd');

const adResource = new adEDM.ResourceIdentifier();

const createData = data => adResource.bulkCreate.bind(adResource)(data);
const updateData = data => adResource.bulkUpdate.bind(adResource)(data);

const bulkUpsertAdSettingsEntityErrorMap = {
  [getBulkUploadErrorType(ResponsiveAd)]: ERROR_ENTITY.Ad,
};

const getErrorsPlaceholders = () => ({
  systemErrors: [],
  serverErrors: [],
});

export const deleteAd = ({ ad, campaign, adGroup }) => viewModelToModelForAd({ adsViewModel: ad, campaign, adGroup }, true)
  .then(deleteAds => updateData(deleteAds))
  .then(result => result, err => err);

export const saveAds = ({
  ads: adsViewModel,
  oldAdsModel,
  campaign,
  adGroup,
  activity = null,
  imageOdata = null,
  permissions,
}, { scenario, i18n }, { isImageFittingEnabled = false } = {}) => splitUpdateAndCreateAds({
  adsViewModel: adsViewModel.data, oldAdsModel, campaign, adGroup, imageOdata, permissions, isImageFittingEnabled,
})
  .then(({ adsNeededToCreate, adsNeededToUpdate }) => {
    // const adsModel = viewModelToModelForAd({ adsViewModel, campaign, adGroup });

    // const modelToSave = adsModel;

    const promises = {};

    if (adsNeededToCreate.length > 0) {
      promises.createAds = createData(adsNeededToCreate);
    }

    if (adsNeededToUpdate.length > 0) {
      promises.updateAds = updateData(adsNeededToUpdate);
    }

    return promises;
  }).then(promises => Promise.props(promises)
    .then((results) => {
      const updatedAds = results.updateAds || [];
      const createdAds = results.createAds || [];

      const result = [...updatedAds, ...createdAds];

      const data = {
        ads: bulkModelToViewModelForAd({
          adsModel: result,
          originalAdsModel: adsViewModel,
        }),
        ...getErrorsPlaceholders(),
      };


      if (activity) {
        activity.signal(`onAdsSavedSuccessfully, redirecting to campaign grid page - adCount: ${result.length}`);
      }
      return data;
    }, (bulkOrSystemError) => {
      if (isBulkWrappedResponse(bulkOrSystemError)) {
        const { entity, errors } = bulkOrSystemError;

        // we need to attach uid to each error to identify which video panel has an error.
        // the uid is stored in the clientid of the error.

        const ads = entity ?
          bulkModelToViewModelForAd({
            adsModel: entity,
            originalAdsModel: adsViewModel,
          }) :
          adsViewModel;

        const serverErrors = modelToViewModelForBulkErrors(
          errors,
          bulkUpsertAdSettingsEntityErrorMap,
          scenario,
          i18n,
          activity
        );

        if (activity) {
          activity.signal(`onAdsSavedUnsuccessfully, ${JSON.stringify(serverErrors)}`);
        }
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
          ads: { ...ads },
          serverErrors,
        });
      }

      // Activity
      // eslint-disable-next-line no-console
      console.warn(`Campaign bulk upsert system error: ${bulkOrSystemError.stack || bulkOrSystemError.message || bulkOrSystemError}`);
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        ads: adsViewModel,
        systemErrors: [i18n.getString(GENERIC_ERROR_MESSAGE_KEY)],
        // TODO: We would want to return the criterions with Ids, but UI's bulk drops them
      });
    }));

export const saveAdsWithMetadata = (data, context) => {
  const { serverContext, activityLogger, i18n } = context;
  const logger = getLoggerWithoutPageInfo(activityLogger, getLogVersion(), 'NativeAdsAdStep');
  const cid = serverContext.CurrentCustomer.Id;
  const aid = serverContext.CurrentAccount.Id;
  const oDataConfig = getODataConfig(serverContext);

  const videoRepository = createVideoRepository({
    oDataConfig,
    aid,
    cid,
  });

  const folderRepository = createFolderRepository({
    i18n,
    logger,
    oDataConfig,
    aid,
    cid,
  });

  const imageRepository = createImageRepository({
    oDataConfig,
    aid,
    cid,
    logger,
    i18n,
    activity: activityLogger,
  });

  const uploadVideoIfNeeded = ({ ads }) => {
    const videoPromises = [];

    if (ads.data.videos && ads.data.videos.length > 0) {
      ads.data.videos.forEach((video, index) => {
        if (video && !video.id) {
          videoPromises.push(uploadVideoMetadata({
            videoMetadata: video,
            newVideoFromMTObject,
            folderRepository,
            imageRepository,
            videoRepository,
          }).then((v) => {
            _.extend(ads.data.videos[index], v);
          }));
        }
      });
    }

    if (_.isEmpty(videoPromises)) {
      return Promise.resolve(ads);
    }

    return Promise.all(videoPromises).then(() => ads);
  };

  return uploadVideoIfNeeded(data).then(() => saveAds(data, context));
};

